import React, { FC, useEffect, useState } from "react";
import Link from "next/link";
import styles from "./Header.module.css";
import {
  SiteNavigation_siteCollection_items_navigationCollection_items,
} from "../../../__generated__/graphql";
import { getOrg } from "utils/site";
import { TopLevelLink } from "./MenuBar/components";
import { useWebTranslation } from "translations";
import classNames from "classnames";
import { useStickyNavigation } from "./useStickyNavigation";
import { MenuBarMobile } from "./MenuBar/Mobile";
import { MenuBarDesktop } from "./MenuBar/Desktop";
import { transformNavigation } from "./transformNavigation";
import { usePartnerTheme } from "../../hooks/usePartnerTheme";

const useScrollPosition = () => {
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    function onScroll() {
      setIsAtTop(window.scrollY === 0);
    }

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return isAtTop;
};

interface IHeaderProps {
  navigationItems: (SiteNavigation_siteCollection_items_navigationCollection_items | null)[];
  siteLanguages: (string | null)[] | null | undefined;
  onMenuOpenChange?: (isOpen: boolean) => void;
  hasHero?: boolean;
}

const Header: FC<IHeaderProps> = ({
  navigationItems,
  siteLanguages,
  onMenuOpenChange,
  hasHero = false,
}) => {
  const { t } = useWebTranslation();
  const partnerTheme = usePartnerTheme();

  const { hide, setIsDisabled } = useStickyNavigation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    onMenuOpenChange?.(isMobileMenuOpen);
  }, [isMobileMenuOpen, onMenuOpenChange]);

  const transformedNavigation = transformNavigation(navigationItems);

  const isAtTop = useScrollPosition();

  const downloadAppUrl = t("navigation.download_app_cta.url");

  const headerClasses = classNames(
    styles.container,
    styles.containerV2,
    hide && !isMenuOpen && styles.hideHeader,
    !isAtTop && styles.scrolled,
    isMenuOpen && styles.isOpen,
    !hasHero && styles.noHero,
  );

  const navItemClassName = styles.navItem;

  return (
    <header className={headerClasses}>
      <div className={styles.content}>
        <>
          <Link href="/" className={styles.flex}>
            <svg
              width="104"
              height="26"
              viewBox="0 0 104 26"
              xmlns="http://www.w3.org/2000/svg"
              className={styles.logoSvg}
            >
              <path d="M8.84668 25.1333C6.24668 25.1333 4.03057 24.2401 2.42521 22.5568C0.697688 20.7532 -0.157345 18.1595 0.0171513 15.2737C0.243997 11.8555 1.79702 8.23119 4.27487 5.34546C6.94467 2.2536 10.3474 0.484375 13.593 0.484375C15.9836 0.484375 18.4963 1.51499 20.7474 3.35293C20.7823 3.38728 20.8172 3.40446 20.8521 3.43881C21.5326 4.00565 22.1782 4.6412 22.8064 5.36263C25.3541 8.2999 26.9594 11.9242 27.1688 15.3253C27.3608 18.3484 26.1044 21.2857 23.7836 23.1752C21.6547 24.91 18.9675 25.5284 16.3849 24.8757C13.5406 24.1542 11.2547 21.9384 10.3823 19.087C9.50977 16.2528 9.26547 10.8593 16.3151 4.95039C15.4078 4.50379 14.4829 4.26331 13.593 4.26331C11.4641 4.26331 9.14333 5.55158 7.20641 7.80176C5.2695 10.0348 4.01312 12.9205 3.83863 15.5142C3.71648 17.3522 4.20507 18.9324 5.1997 19.9802C6.07219 20.8906 7.29366 21.3544 8.82923 21.3544C9.89366 21.3544 10.7487 22.1961 10.7487 23.2439C10.7487 24.2917 9.89366 25.1333 8.84668 25.1333ZM19.4212 7.32081C14.9192 10.8764 13.0172 14.621 14.0292 18.0049C14.5178 19.6023 15.7742 20.8219 17.3098 21.217C18.6883 21.5605 20.1541 21.217 21.3232 20.2551C22.7017 19.1386 23.4521 17.3693 23.3299 15.5486C23.1554 12.6113 21.55 9.51946 19.4212 7.32081Z" />
              <path d="M46.2416 21.5262H35.9463V4.09155H39.6107V18.2454H46.2416V21.5262Z" />
              <path d="M60.2014 8.66066V21.5434H56.9383V19.9631C56.1182 21.1483 54.635 21.8354 52.7853 21.8354C48.9987 21.8354 46.7651 18.8294 46.7651 15.102C46.7651 11.3746 48.9812 8.36865 52.7853 8.36865C54.635 8.36865 56.1007 9.05573 56.9383 10.2409V8.66066H60.2014ZM53.5356 11.4262C51.5638 11.4262 50.3074 13.0064 50.3074 15.0848C50.3074 17.1633 51.5638 18.7435 53.5356 18.7435C55.5074 18.7435 56.7638 17.1633 56.7638 15.0848C56.7638 13.0064 55.5074 11.4262 53.5356 11.4262Z" />
              <path d="M61.405 17.3521H64.9997C65.052 18.211 65.6976 19.0183 67.1634 19.0183C68.507 19.0183 69.205 18.5545 69.205 17.8331C69.205 15.6688 61.946 17.7128 61.946 12.5941C61.946 10.258 63.9701 8.33423 67.1809 8.33423C71.107 8.33423 72.4507 10.7905 72.5554 12.68H69.1352C69.0654 11.8727 68.4372 11.1341 67.1634 11.1341C66.0117 11.1341 65.3661 11.6666 65.3661 12.388C65.3661 14.5523 72.6077 12.388 72.6077 17.6785C72.6077 19.9802 70.6185 21.801 67.0587 21.801C62.9755 21.8181 61.5795 19.7569 61.405 17.3521Z" />
              <path d="M73.4631 17.3521H77.0578C77.1101 18.211 77.7557 19.0183 79.2215 19.0183C80.5651 19.0183 81.2631 18.5545 81.2631 17.8331C81.2631 15.6688 74.0041 17.7128 74.0041 12.5941C74.0041 10.258 76.0282 8.33423 79.239 8.33423C83.1651 8.33423 84.5087 10.7905 84.6134 12.68H81.1933C81.1235 11.8727 80.4953 11.1341 79.2215 11.1341C78.0698 11.1341 77.4242 11.6666 77.4242 12.388C77.4242 14.5523 84.6658 12.388 84.6658 17.6785C84.6658 19.9802 82.6765 21.801 79.1168 21.801C75.0336 21.8181 73.6376 19.7569 73.4631 17.3521Z" />
              <path d="M85.7651 4.8989C85.7651 3.66216 86.6725 2.78613 87.9114 2.78613C89.1504 2.78613 90.0578 3.67934 90.0578 4.8989C90.0578 6.11847 89.1504 6.99449 87.9114 6.99449C86.6725 6.99449 85.7651 6.11847 85.7651 4.8989ZM86.149 8.66066H89.6913V21.5434H86.149V8.66066Z" />
              <path d="M94.4553 16.0981C94.6996 17.8674 95.8512 18.8293 97.6311 18.8293C98.713 18.8293 99.7251 18.3655 100.144 17.4208H103.721C102.953 20.0317 100.545 21.8181 97.5264 21.8181C93.6526 21.8181 90.9304 19.0182 90.9304 15.0675C90.9304 11.2542 93.6351 8.35132 97.4915 8.35132C101.365 8.35132 103.983 11.2542 103.983 15.0675C103.983 15.6344 103.93 16.0981 103.93 16.0981H94.4553ZM94.5076 13.5903H100.44C100.196 12.1474 99.1143 11.1512 97.474 11.1512C95.8512 11.1683 94.8042 12.1131 94.5076 13.5903Z" />
            </svg>
          </Link>

          <div className={styles.tabletCta}>
            <Link
              href="signup"
              className={classNames(styles.linkButton, styles.linkButtonV2)}
            >
              {t("home.sections.0.btn")}
            </Link>
          </div>
          <div className={styles.mobileContainer}>
            <MenuBarMobile
              navigationItems={transformedNavigation}
              onMenuToggle={(isOpen) => {
                setIsDisabled(isOpen);
                setIsMobileMenuOpen(isOpen);
              }}
            />
          </div>
          <div className={styles.desktopContainer}>
            <MenuBarDesktop
              navigationItems={transformedNavigation}
              onMenuToggle={(isExpanded) => {
                setIsMenuOpen(isExpanded);
                setIsDisabled(isExpanded);
              }}
              className={navItemClassName}
            />
          </div>
          <div className={styles.desktopContainer}>
            {downloadAppUrl.length > 0 ? (
              <TopLevelLink
                href={t(`${getOrg()}.navigation.download_app_cta.url`)}
                label={t("navigation.download_app_cta.label")}
                className={navItemClassName}
              />
            ) : null}
            <Link
              href="signup"
              className={classNames(styles.linkButton, styles.linkButtonV2)}
            >
              {t("home.sections.0.btn")}
            </Link>
          </div>
        </>
      </div>
    </header>
  );
};

export default Header;
